import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Rating,
  Button,
  Avatar,
  Chip,
  Paper,
  Container,
  CircularProgress,
} from '@mui/material';
import {
  Star,
  Verified,
  ArrowForward,
} from '@mui/icons-material';
import useFeaturedReviews from '../hooks/useFeaturedReviews';

const ReviewsSection = ({
  title = 'What Our Users Say',
  subtitle = 'Real feedback from job seekers who used JobMockAI',
  maxReviews = 6,
  showViewAll = true,
  onViewAll,
  cacheBust = true,
  sectionId,
  reviews: reviewsProp,
  reviewStats: reviewStatsProp,
  loading: loadingProp,
}) => {
  const useExternalData = reviewsProp !== undefined;
  const fetched = useFeaturedReviews(maxReviews, { cacheBust, enabled: !useExternalData });

  const reviews = useExternalData ? reviewsProp : fetched.reviews;
  const reviewStats = useExternalData ? reviewStatsProp : fetched.reviewStats;
  const loading = useExternalData ? (loadingProp ?? false) : fetched.loading;

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }} id={sectionId}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ py: 6, bgcolor: 'grey.50' }} id={sectionId} component={sectionId ? 'section' : 'div'}>
      <Container maxWidth="lg">
        <Box sx={{ textAlign: 'center', mb: 5 }}>
          <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 'bold' }}>
            {title}
          </Typography>
          <Typography variant="h6" color="text.secondary" paragraph>
            {subtitle}
          </Typography>

          {reviewStats && reviewStats.totalReviews > 0 && (
            <Paper
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 2,
                p: 2,
                mt: 2,
                bgcolor: 'primary.main',
                color: 'white',
              }}
            >
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                  {reviewStats?.averageRating != null ? reviewStats.averageRating.toFixed(1) : '0.0'}
                </Typography>
                <Rating
                  value={reviewStats.averageRating || 0}
                  readOnly
                  precision={0.1}
                  sx={{
                    '& .MuiRating-iconFilled': { color: 'white' },
                    '& .MuiRating-iconEmpty': { color: 'rgba(255,255,255,0.3)' },
                  }}
                />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  Excellent
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Based on {reviewStats.totalReviews} reviews
                </Typography>
              </Box>
            </Paper>
          )}
        </Box>

        {reviews.length > 0 ? (
          <Grid container spacing={3} sx={{ mb: 4 }}>
            {reviews.map((review) => (
              <Grid item xs={12} md={6} lg={4} key={review.id}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 4,
                    },
                  }}
                >
                  <CardContent sx={{ flexGrow: 1, p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                        {review.userName ? review.userName.charAt(0).toUpperCase() : 'U'}
                      </Avatar>
                      <Box sx={{ flexGrow: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                            {review.userName || 'Anonymous User'}
                          </Typography>
                          {review.verified && (
                            <Chip
                              icon={<Verified />}
                              label="Verified"
                              size="small"
                              color="success"
                              sx={{ height: 20, fontSize: '0.7rem' }}
                            />
                          )}
                        </Box>
                        <Rating value={review.rating} readOnly size="small" sx={{ mt: 0.5 }} />
                      </Box>
                    </Box>

                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', fontSize: '1rem' }}>
                      {review.title}
                    </Typography>

                    {review.comment && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          mb: 2,
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}
                      >
                        &ldquo;{review.comment}&rdquo;
                      </Typography>
                    )}

                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mt: 'auto',
                        pt: 2,
                        borderTop: '1px solid',
                        borderColor: 'divider',
                      }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(review.updatedAt || review.createdAt)}
                      </Typography>
                      {review.interviewCount > 0 && (
                        <Chip
                          label={`${review.interviewCount} interviews`}
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: '0.7rem' }}
                        />
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Star sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No reviews yet
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Be the first to share your experience with JobMockAI!
            </Typography>
          </Box>
        )}

        {showViewAll && reviews.length > 0 && (
          <Box sx={{ textAlign: 'center' }}>
            <Button
              variant="outlined"
              size="large"
              endIcon={<ArrowForward />}
              onClick={onViewAll}
              sx={{
                px: 4,
                py: 1.5,
                borderRadius: 2,
                textTransform: 'none',
                fontSize: '1.1rem',
              }}
            >
              View All Reviews
            </Button>
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default ReviewsSection;
